import memoize, { type MemoizedFn } from 'memoize-one';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls';
import { performHttpFetch } from '@atlassian/conversation-assistant-utils/httpUtils';

import { LS_PROVIDER_KEY } from '../../../constants/private';

import { PROVIDERS_API } from './constants';
import type { ProviderRegexResponse } from './types';

/**
 * Check if the url is supported by Linking Platform by
 * testing it against any of the regex patterns
 * @param url string
 * @returns boolean
 */
export const isSupportedByLinkingPlatform = (url: string): boolean => {
	const cachedProviderRegex = getProviderRegexFromLs();

	if (!cachedProviderRegex.length || !url.length) {
		return false;
	}

	const regexPattern = new RegExp(cachedProviderRegex, 'g');
	return regexPattern.test(url);
};

/**
 * Get the currently stored provider regex patterns from LS
 * @returns Json<ProviderRegexStorage>
 */
export const getProviderRegexFromLs = (): string => {
	if (fg('csm-cm04-use-atlassian-browser-storage-controls')) {
		try {
			const value = AtlBrowserStorageLocal.getItem(LS_PROVIDER_KEY);
			if (value) {
				return JSON.parse(value);
			}
		} catch {
			// Fall through and return '' on any storage or JSON parse failure.
		}
		return '';
	}
	// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
	if (localStorage.getItem(LS_PROVIDER_KEY)) {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		return JSON.parse(`${localStorage.getItem(LS_PROVIDER_KEY)}`);
	}
	return '';
};

/**
 * setProviderRegexLs - set the provider regex patterns in LS for
 * easy access within the synchronous md tokenizers
 * @param providerRegex string - combined regex patterns from Linking Platform to identify supported urls
 */
export const setProviderRegexLs = (providerRegex: string): void => {
	if (fg('csm-cm04-use-atlassian-browser-storage-controls')) {
		// `setItem` never throws and write failures are already logged internally — see https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/2cfe787/platform/packages/policy-platform/browser-storage-controls/src/services/web-storage-service/utils/set-storage-item.ts#lines-61:65
		AtlBrowserStorageLocal.setItem(LS_PROVIDER_KEY, JSON.stringify(providerRegex));
		return;
	}
	// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
	localStorage?.setItem(LS_PROVIDER_KEY, JSON.stringify(providerRegex));
};

/**
 * hydrateLinkingPlatformCache - fetch the provider regex patterns from Linking Platform
 */
export const hydrateLinkingPlatformCache = async (): Promise<void> => {
	const providerRegex = await getProviderRegexFromLinkingPlatform();
	setProviderRegexLs(providerRegex);
};

/**
 * reduceProviderRegex
 * @param data - ProviderRegexResponse
 * @returns string - combined regex patterns from Linking Platform for a single regex test
 * to avoid individual tests on a loop of the providers
 */
export const reduceProviderRegex = (data: ProviderRegexResponse): string => {
	const sources = data.providers.reduce((acc, provider) => {
		const providerSources = provider.patterns.map((pattern) => pattern.source);
		return [...acc, ...providerSources];
	}, [] as string[]);

	return sources.join('|');
};

/**
 * Get a list of regex patterns for all supported providers from Linking Platform
 * This is called to hydrate localStorage with every `ContentRenderer` mount, so we're
 * memoizing the response to avoid unnecessary network requests
 * @returns ProviderRegexStorage
 */
export const getProviderRegexFromLinkingPlatform: MemoizedFn<() => Promise<string>> = memoize(
	async (): Promise<string> => {
		try {
			const data: ProviderRegexResponse = await performHttpFetch({
				path: PROVIDERS_API,
				options: {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						type: 'resolve',
					}),
				},
			});
			return !data ? '' : reduceProviderRegex(data);
		} catch (error) {
			return '';
		}
	},
);
